'use client';

// Conversion-choice dialog — shown when a file is staged for library import.
//
// Replaces the old cramped inline two-pill chooser (ConverterPill) with a
// focused, explained decision: Fast (MarkItDown) vs Premium (Docling). The
// dialog is the right affordance here, not laziness — a file is staged and the
// user must make a bounded, billable choice that benefits from explanation the
// inline pills had no room for.
//
// Option model mirrors the backend upload wire, which accepts exactly
// `markitdown | docling`. The escalation lane *within* Docling
// (basic → vlm_verify → vlm → mistral_ocr) is normally resolved server-side
// from the user's tier + docling.vlm_mode setting. This dialog surfaces an
// optional per-upload **override** of that lane (Auto / Full / Mistral OCR) as
// a subordinate refinement under Premium — it travels as `docling_vlm_mode` on
// the SetConverterChoice request and supersedes the setting for this one
// document. Both locks here are DISPLAY HINTS: the Premium lock follows the
// server's capability map (BILL-20a; reconvertCore refuses with 241), and the
// lane lock follows the gate tier (see doclingTier.ts; the backend
// `docling.SelectMode` clamp is the authoritative gate).

import { Check, FileText, Lock, ScanText, Sparkles, Zap } from 'lucide-react';
import { useTranslations } from 'next-intl';
import * as React from 'react';
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
} from '@/components/ui/Dialog';
import { PlanUpsell, useCapability, useGateTier } from '@/features/billing';
import { useSubscriptionStore } from '@/features/settings/subscriptionStore';
// By file path, not the alwayson barrel: the barrel exports components that
// import features/settings, which risks a cycle (plan F5 step 10).
import { useAlwaysOnSpaceExists } from '@/features/workspaces/alwayson/alwaysOnSpaceStore';
import { cn } from '@/lib/utils';
import { type DoclingVlmMode, laneAvailable, tierNameToDoclingLevel } from './doclingTier';

export type ConverterChoice = 'markitdown' | 'docling';

export type ConverterSuggestionReason =
  | ''
  | 'scanned_pdf'
  | 'image_dense_pdf'
  | 'text_native'
  | 'non_pdf'
  | 'exceeds_per_doc_cap';

export interface ConverterSuggestion {
  suggested: boolean;
  reason: ConverterSuggestionReason;
  estimatedCredits: number;
  estimatedSecs: number;
}

interface ConversionChoiceDialogProps {
  open: boolean;
  /** The staged file. Its name + size head the dialog. */
  file: File | null;
  /**
   * Preflight suggestion for the staged file. `null` means Premium does not
   * apply (non-PDF) — the Premium option renders disabled with a "PDFs only"
   * note. A `text_native` / `non_pdf` / `exceeds_per_doc_cap` reason likewise
   * disables Premium with the matching explanation.
   */
  suggestion: ConverterSuggestion | null;
  /**
   * Confirm the import. `vlmMode` is the per-upload Docling escalation-lane
   * override — always `''` for MarkItDown; `'' | 'full' | 'ocr'` for Premium,
   * where `''` means "use my saved default".
   */
  onConfirm: (choice: ConverterChoice, vlmMode: DoclingVlmMode) => void;
  onCancel: () => void;
  /** Page count — only used to phrase the per-doc-cap disabled note. */
  pageCount?: number;
  /** Per-tier Premium page cap — only used in the cap note. */
  capMax?: number;
}

/** Human file size: 820 KB, 2.4 MB. */
function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Why Premium is unavailable for this file, or null when it is available. */
function premiumUnavailableNote(
  t: ReturnType<typeof useTranslations>,
  suggestion: ConverterSuggestion | null,
  pageCount?: number,
  capMax?: number,
): string | null {
  if (suggestion === null) return t('conversionChoiceDialog.premiumAppliesPdfs');
  switch (suggestion.reason) {
    case 'non_pdf':
      return t('conversionChoiceDialog.premiumAppliesPdfs');
    case 'text_native':
      return t('conversionChoiceDialog.premiumTextNative');
    case 'exceeds_per_doc_cap':
      return typeof pageCount === 'number' && typeof capMax === 'number'
        ? t('conversionChoiceDialog.premiumCapNote', { pageCount, capMax })
        : t('conversionChoiceDialog.premiumCapNoteGeneric');
    default:
      return null;
  }
}

interface OptionRowProps {
  selected: boolean;
  disabled?: boolean;
  icon: React.ReactNode;
  title: string;
  meta: string;
  recommended?: boolean;
  onSelect: () => void;
  onKeyNav: (e: React.KeyboardEvent<HTMLButtonElement>) => void;
  children: React.ReactNode;
}

function OptionRow({
  selected,
  disabled,
  icon,
  title,
  meta,
  recommended,
  onSelect,
  onKeyNav,
  children,
}: OptionRowProps) {
  const t = useTranslations('documents');
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-label={title}
      disabled={disabled}
      tabIndex={disabled ? -1 : selected ? 0 : -1}
      onClick={disabled ? undefined : onSelect}
      onKeyDown={onKeyNav}
      className={cn(
        'group flex w-full items-start gap-3 rounded-md border p-4 text-left transition-colors duration-150',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        disabled
          ? 'cursor-not-allowed border-border opacity-60'
          : selected
            ? 'border-accent bg-accent/[0.06] cursor-pointer'
            : 'border-border hover:bg-hover cursor-pointer',
      )}
    >
      {/* Selection indicator: filled check when chosen, hollow ring otherwise. */}
      <span
        aria-hidden
        className={cn(
          'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-colors duration-150',
          selected ? 'border-accent bg-accent text-accent-foreground' : 'border-border',
        )}
      >
        {selected && <Check className="h-3 w-3" strokeWidth={3} />}
      </span>

      <span
        aria-hidden
        className={cn(
          'mt-0.5 shrink-0 transition-colors duration-150',
          selected ? 'text-accent' : 'text-muted-foreground',
        )}
      >
        {icon}
      </span>

      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="flex items-baseline gap-2">
          <span className="font-medium text-foreground">{title}</span>
          {recommended && (
            <span className="rounded-full bg-accent/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-accent">
              {t('conversionChoiceDialog.recommended')}
            </span>
          )}
          <span className="ml-auto shrink-0 text-xs tabular-nums text-muted-foreground">
            {meta}
          </span>
        </span>
        {children}
      </span>
    </button>
  );
}

/** The Premium engine lanes offered as a per-upload override. */
const ENGINE_OPTIONS: ReadonlyArray<{
  value: DoclingVlmMode;
  /** Null = always available (Auto). Otherwise the lane whose tier gates it. */
  lane: 'full' | 'ocr' | null;
}> = [
  { value: '', lane: null },
  { value: 'full', lane: 'full' },
  { value: 'ocr', lane: 'ocr' },
];

interface EngineSelectorProps {
  value: DoclingVlmMode;
  onChange: (v: DoclingVlmMode) => void;
  /** The user's Docling ceiling (display hint; backend clamp is authoritative). */
  tierLevel: number;
}

/**
 * Subordinate engine picker shown under Premium. A small radiogroup — Auto
 * (defer to the saved default) / Full / Mistral OCR — with lanes above the
 * user's plan locked. Visually indented and quieter than the two top-level
 * options: it is a refinement, not a third choice.
 */
function EngineSelector({ value, onChange, tierLevel }: EngineSelectorProps): React.ReactElement {
  const t = useTranslations('documents');
  const labelFor = (v: DoclingVlmMode): string =>
    t(
      v === ''
        ? 'conversionChoiceDialog.engineOptionAuto'
        : v === 'full'
          ? 'conversionChoiceDialog.engineOptionFull'
          : 'conversionChoiceDialog.engineOptionOcr',
    );
  const isAvailable = (lane: 'full' | 'ocr' | null): boolean =>
    lane === null ? true : laneAvailable(lane, tierLevel);
  const anyLocked = ENGINE_OPTIONS.some((o) => !isAvailable(o.lane));

  // Roving arrow-key selection across the *enabled* lanes only.
  const move = (e: React.KeyboardEvent<HTMLButtonElement>, currentIdx: number) => {
    if (!['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft'].includes(e.key)) return;
    e.preventDefault();
    const dir = e.key === 'ArrowDown' || e.key === 'ArrowRight' ? 1 : -1;
    let i = currentIdx;
    for (let step = 0; step < ENGINE_OPTIONS.length; step++) {
      i = (i + dir + ENGINE_OPTIONS.length) % ENGINE_OPTIONS.length;
      const opt = ENGINE_OPTIONS[i]!;
      if (isAvailable(opt.lane)) {
        onChange(opt.value);
        break;
      }
    }
  };

  return (
    <div className="mt-2.5 flex flex-col gap-1.5">
      <span id="premium-engine-label" className="text-xs font-medium text-muted-foreground">
        {t('conversionChoiceDialog.engineHeading')}
      </span>
      <div
        role="radiogroup"
        aria-labelledby="premium-engine-label"
        className="flex flex-wrap items-center gap-1.5"
      >
        {ENGINE_OPTIONS.map((opt, idx) => {
          const available = isAvailable(opt.lane);
          const selected = value === opt.value;
          return (
            <button
              key={opt.value || 'auto'}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={labelFor(opt.value)}
              disabled={!available}
              tabIndex={selected ? 0 : -1}
              onClick={available ? () => onChange(opt.value) : undefined}
              onKeyDown={(e) => move(e, idx)}
              className={cn(
                'inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs transition-colors duration-150',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                !available
                  ? 'cursor-not-allowed border-border text-muted-foreground/60'
                  : selected
                    ? 'border-accent bg-accent/[0.08] text-accent cursor-pointer'
                    : 'border-border text-muted-foreground hover:bg-hover cursor-pointer',
              )}
            >
              {labelFor(opt.value)}
              {!available && <Lock aria-hidden className="h-3 w-3" />}
            </button>
          );
        })}
      </div>
      {anyLocked && (
        <span className="text-[11px] leading-snug text-muted-foreground/90">
          {t('conversionChoiceDialog.upgradeForThis')}
        </span>
      )}
    </div>
  );
}

export function ConversionChoiceDialog({
  open,
  file,
  suggestion,
  onConfirm,
  onCancel,
  pageCount,
  capMax,
}: ConversionChoiceDialogProps): React.ReactElement | null {
  const t = useTranslations('documents');
  // AON-08: "readable only by you" holds only with no always-on space. While the
  // status is not known (demo guest, before unlock) the scoped wording is used,
  // so the claim never over-promises. Read-only: auto-mount fills the store.
  const alwaysOnSpace = useAlwaysOnSpaceExists();
  // BILL-20a (KD-S7): the server's capability verdict, over the best-of gate
  // tier the BE gates on. `unknown` (no map yet, demo guest, older backend)
  // never locks: the server stays authoritative (reconvertCore refuses with 241,
  // the indexer falls back). Display hints only.
  const docling = useCapability('docling_premium');
  const planLocksPremium = docling.entitlement === 'locked';
  // Engine-lane ceiling from the same best-of tier; the owner-first display
  // tier is only the fallback when the server sent none (demo guest).
  const gateTier = useGateTier();
  const tierName = useSubscriptionStore((s) => s.tierName);
  const tierLevel = tierNameToDoclingLevel(gateTier || tierName);
  // A file-type reason is more specific than the plan, so it wins: a locked
  // user dropping a .docx still sees "PDFs only", not the upgrade prompt.
  const fileNote = premiumUnavailableNote(t, suggestion, pageCount, capMax);
  const premiumEnabled = fileNote === null && !planLocksPremium;
  const recommended = suggestion?.suggested === true && premiumEnabled;

  // Default to Fast (free) every time the dialog opens — never preselect the
  // billable option (no dark patterns). The Recommended chip guides instead.
  const [choice, setChoice] = React.useState<ConverterChoice>('markitdown');
  // Per-upload Docling engine override. Auto ('') = use the saved default.
  const [engine, setEngine] = React.useState<DoclingVlmMode>('');
  React.useEffect(() => {
    if (open) {
      setChoice('markitdown');
      setEngine('');
    }
  }, [open, file]);

  // If Premium stops being available while the dialog is open (the capability
  // map turned locked, or the file changed), a Premium choice made earlier must
  // not be sent: fall back to Fast (the server would refuse it anyway).
  React.useEffect(() => {
    if (!premiumEnabled && choice === 'docling') setChoice('markitdown');
  }, [premiumEnabled, choice]);

  // If the picked engine sits above the user's plan (e.g. their tier changed,
  // or a lane was pre-selected), fall back to Auto so we never send a lane the
  // server would only clamp away.
  React.useEffect(() => {
    if (engine !== '' && !laneAvailable(engine, tierLevel)) setEngine('');
  }, [engine, tierLevel]);

  // Roving selection between the two options via arrow keys (a radiogroup
  // affordance users expect). Skips Premium when it is disabled.
  const move = React.useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft'].includes(e.key)) {
        e.preventDefault();
        if (!premiumEnabled) {
          setChoice('markitdown');
          return;
        }
        setChoice((c) => (c === 'markitdown' ? 'docling' : 'markitdown'));
      }
    },
    [premiumEnabled],
  );

  if (!open || !file) return null;

  const premiumMeta = suggestion
    ? t('conversionChoiceDialog.premiumMeta', {
        credits: suggestion.estimatedCredits,
        secs: suggestion.estimatedSecs,
      })
    : t('conversionChoiceDialog.pdfsOnly');

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogOverlay onClick={onCancel} />
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('conversionChoiceDialog.importTitle')}</DialogTitle>
          <DialogDescription>{t('conversionChoiceDialog.importDescription')}</DialogDescription>
        </DialogHeader>

        {/* Staged file, headed so the choice has a subject. */}
        <div className="mb-4 flex items-center gap-2.5 rounded-md bg-hover/60 px-3 py-2">
          <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="min-w-0 flex-1 truncate text-sm text-foreground" title={file.name}>
            {file.name}
          </span>
          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
            {formatSize(file.size)}
          </span>
        </div>

        <div
          role="radiogroup"
          aria-label={t('conversionChoiceDialog.conversionQuality')}
          className="flex flex-col gap-2"
        >
          <OptionRow
            selected={choice === 'markitdown'}
            icon={<Zap className="h-[18px] w-[18px]" />}
            title={t('conversionChoiceDialog.fastTitle')}
            meta={t('conversionChoiceDialog.fastMeta')}
            onSelect={() => setChoice('markitdown')}
            onKeyNav={move}
          >
            <span className="text-sm leading-snug text-muted-foreground">
              {t('conversionChoiceDialog.fastDescription')}
            </span>
          </OptionRow>

          <OptionRow
            selected={choice === 'docling'}
            disabled={!premiumEnabled}
            icon={<ScanText className="h-[18px] w-[18px]" />}
            title={t('conversionChoiceDialog.premiumTitle')}
            meta={premiumMeta}
            recommended={recommended}
            onSelect={() => setChoice('docling')}
            onKeyNav={move}
          >
            <span className="text-sm leading-snug text-muted-foreground">
              {t('conversionChoiceDialog.premiumDescription')}
            </span>
            {premiumEnabled ? (
              <span className="mt-1.5 flex items-start gap-1.5 text-xs leading-snug text-muted-foreground">
                <Sparkles className="mt-px h-3 w-3 shrink-0 text-accent" />
                <span>
                  {t.rich('conversionChoiceDialog.premiumTopPlansHint', {
                    highlight: (chunks) => (
                      <span className="font-medium text-foreground">{chunks}</span>
                    ),
                  })}
                </span>
              </span>
            ) : fileNote !== null ? (
              <span className="mt-1 block text-xs leading-snug text-muted-foreground/90">
                {fileNote}
              </span>
            ) : null}
          </OptionRow>
        </div>

        {/* BILL-20a (KD-S7): the plan lock's upgrade prompt. Outside the Premium
            radio on purpose: a link inside a <button> is invalid content and
            nested-interactive for assistive tech. */}
        {fileNote === null && planLocksPremium && (
          <div className="mt-1 pl-11 pr-1">
            <PlanUpsell
              variant="inline"
              feature="docling_premium"
              requiredTier={docling.requiredTier}
            />
          </div>
        )}

        {/* Engine override — a subordinate refinement, shown only when Premium
            is the chosen (and available) path. Kept out of the quality
            radiogroup above (it is its own small radiogroup) and indented to
            sit under Premium rather than read as a third top-level option. */}
        {choice === 'docling' && premiumEnabled && (
          <div className="mt-1 pl-11 pr-1">
            <EngineSelector value={engine} onChange={setEngine} tierLevel={tierLevel} />
          </div>
        )}

        <p className="mt-4 text-xs leading-snug text-muted-foreground">
          {t(
            alwaysOnSpace === 'no'
              ? 'conversionChoiceDialog.encryptedNote'
              : 'conversionChoiceDialog.encryptedNoteWithSpace',
          )}
        </p>

        <DialogFooter>
          <Button type="button" variant="secondary" size="sm" onClick={onCancel}>
            {t('conversionChoiceDialog.cancel')}
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={() => onConfirm(choice, choice === 'docling' ? engine : '')}
          >
            {t('conversionChoiceDialog.import')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
